/**
 * Canal iFood: lojas conectadas, confirmação automática e pedidos. Os pedidos chegam por eventos a cada 30 segundos.
 */
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShoppingCart, SlidersHorizontal } from 'lucide-react';
import { api, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag } from '../components/ui.tsx';
import { HomeBoard } from './Overview.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { Head } from './MeliChannel.tsx';
import { can } from '@berytus/contracts';

interface IfoodAccount {
  id: string; name: string | null; merchantId: string; status: string; lastError: string | null; store: string | null; autoConfirm: boolean;
  orders: { total: number; open: number; toConfirm: number; lastEventAt: string | null };
}

export function IfoodSummary() {
  const admin = can(useMe().tenant.access, 'channel_settings', 'write');
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['ifood', 'overview'],
    queryFn: () => api<{ configured: boolean; accounts: IfoodAccount[] }>('/channels/ifood/overview'),
    refetchInterval: 30_000,
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; autoConfirm: boolean }) => api(`/channels/ifood/accounts/${v.id}`, { method: 'PUT', json: { autoConfirm: v.autoConfirm } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ifood'] }),
  });
  return (
    <>
      <Head title="iFood">
        <p>Pedidos de cada loja chegam a cada 30 segundos, no mesmo padrão dos outros canais (painel, ERP e API). Confirmação, pronto para retirada, despacho e cancelamento ficam no pedido.</p>
      </Head>
      {!!q.data?.accounts.length && <div className="stack-y" style={{ marginBottom: 'var(--gap)' }}><HomeBoard channel="ifood" /></div>}
      {q.isPending ? <Loading /> : q.error ? <ErrorBox error={q.error} /> : !q.data.accounts.length ? (
        <div className="panel empty"><b>Nenhuma loja do iFood conectada</b><Link to="/canais">Conecte em Integração de canais.</Link></div>
      ) : (
        <div className="stack-y">
          {q.data.accounts.map((a) => (
            <section key={a.id} className="panel" style={{ display: 'grid', gap: 12 }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0, fontSize: 17 }}>{a.name ?? 'Loja'}</h2>
                <span className="small muted">Loja nº {a.merchantId.slice(0, 8)}…{a.store ? ` · vende o estoque de ${a.store}` : ''}</span>
                {a.status !== 'active' && <Tag tone="err">precisa reconectar</Tag>}
                <span style={{ flex: 1 }} />
                {a.orders.toConfirm > 0 && <Tag tone="warn">{int(a.orders.toConfirm)} aguardando confirmação</Tag>}
              </div>
              {a.lastError && <div className="notice err small">{a.lastError}</div>}
              <dl className="facts">
                <div><dt>Pedidos</dt><dd>{int(a.orders.total)} · {int(a.orders.open)} em aberto</dd></div>
                <div><dt>Último movimento</dt><dd>{a.orders.lastEventAt ? dateTime(a.orders.lastEventAt) : '—'}</dd></div>
                <div><dt>Confirmação</dt><dd>
                  <label style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    <input type="checkbox" className="check" checked={a.autoConfirm} disabled={!admin || toggle.isPending}
                      onChange={(e) => toggle.mutate({ id: a.id, autoConfirm: e.target.checked })} />
                    Confirmar automaticamente os pedidos novos
                  </label>
                  <div className="small muted">{a.autoConfirm ? 'A Berytus confirma no iFood assim que o pedido chega.' : 'Confirme cada pedido na tela do pedido (o iFood cancela pedidos não confirmados a tempo).'}</div>
                </dd></div>
              </dl>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Link className="btn sm primary" to={`/pedidos?channel=ifood`}><ShoppingCart size={14} /> Ver pedidos</Link>
                <Link className="btn sm quiet" to="/canal/ifood/configuracoes"><SlidersHorizontal size={14} /> Configurações do canal</Link>
              </div>
            </section>
          ))}
          {toggle.error && <ErrorBox error={toggle.error} />}
          <p className="small muted">Catálogo, preço e estoque no iFood: próxima etapa (API de itens, a mesma que a Qualidoc já usa).</p>
        </div>
      )}
    </>
  );
}
